import { useState } from 'react'
import { EventAttendeeState, EventType } from 'graphql/generated/scheme'
import { PieImage, RSVPStatus } from 'components/common'
import { ContentModal } from 'components/organisms/modals/content-modal'
import { Button } from 'components/atoms/button'
import { ShareLinkCard } from 'components/molecules/share-link-card'

interface RSVPSuccessModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	attendeeState?: EventAttendeeState | null
	eventAvatarUrl?: string | null
	isGettingFreeTicket?: boolean
	/** Opened by a paid checkout, not a free claim or RSVP. */
	isPaidPurchase?: boolean
	eventType?: EventType | null
	contestEntrySelected?: boolean
	shareableLink?: string | null
	contestName?: string | null
	onClose?: () => void
}

// The modal only opens right after the attendee's own RSVP or purchase, so an Approved state here means no
// approval step was involved: say the tickets are confirmed, never that the attendee was "approved".
const STATUS_LABEL: Partial<Record<EventAttendeeState, string>> = {
	[EventAttendeeState.Approved]: 'Confirmed',
	[EventAttendeeState.InviteeAccepted]: 'Pending Approval',
}

function contestRunningText(contestName?: string | null) {
	return contestName
		? `You're in the running for ${contestName}. We'll notify you if you're selected. Keep an eye on your account, email, and texts around the event.`
		: `You're in the running. We'll notify you if you're selected. Keep an eye on your account, email, and texts around the event.`
}

function InviteeAcceptedBody({
	isContest,
	isGettingFreeTicket,
	isPaidPurchase,
	contestName,
}: Readonly<{ isContest: boolean; isGettingFreeTicket: boolean; isPaidPurchase: boolean; contestName?: string | null }>) {
	const message = isContest && isGettingFreeTicket
		? contestRunningText(contestName)
		: isContest
		? `We will notify you when your status changes.`
		: `Your tickets are pending approval from the host. We'll notify you when your status changes.`

	return (
		<div className="flex flex-col gap-4">
			<span>{message}</span>
			{isPaidPurchase && !isContest && (
				<span className="text-sm text-secondary-text italic">
					You will only be charged if you are approved for the event.
				</span>
			)}
		</div>
	)
}

function ApprovedBody({ isContest, isContestPurchaseConfirmed, contestName }: { isContest: boolean; isContestPurchaseConfirmed: boolean; contestName?: string | null }) {
	if (isContestPurchaseConfirmed) {
		const text = contestName
			? `You're all set, and you're entered in ${contestName}. We'll notify you if you're selected. Keep an eye on your account, email, and texts around the event.`
			: `You're all set and entered. We'll notify you if you're selected. Keep an eye on your account, email, and texts around the event.`
		return <div className="flex flex-col gap-4"><span>{text}</span></div>
	}
	if (isContest) {
		return <div className="flex flex-col gap-4"><span>{contestRunningText(contestName)}</span></div>
	}
	return (
		<div className="flex flex-col gap-4">
			<span>You have your tickets.</span>
			<span className="text-sm text-secondary-text italic">
				You can find your tickets on the top of the event page.
			</span>
		</div>
	)
}

function getTitle(
	isContestPurchaseConfirmed: boolean,
	isContest: boolean,
	isPaidPurchase: boolean,
	attendeeState?: EventAttendeeState | null
) {
	if (isContestPurchaseConfirmed) return `Your purchase is confirmed`
	if (isContest) return `You're entered`
	if (attendeeState === EventAttendeeState.Approved) return isPaidPurchase ? `Purchase complete` : `You're all set!`
	if (attendeeState === EventAttendeeState.InviteeAccepted) return `Pending approval`
	return `Thanks for RSVP'ing!`
}

export function RSVPSuccessModal({
	open,
	setOpen,
	attendeeState,
	eventAvatarUrl,
	isGettingFreeTicket = false,
	isPaidPurchase = false,
	eventType,
	contestEntrySelected = false,
	shareableLink,
	contestName,
	onClose,
}: RSVPSuccessModalProps) {
	const handleClose = () => {
		setOpen(false)
		onClose?.()
	}

	const isContest = eventType === EventType.Contest
	// ENG-2684: paid ticket + checkbox was checked → purchase confirmed variant
	const isContestPurchaseConfirmed = isContest && contestEntrySelected
	const title = getTitle(isContestPurchaseConfirmed, isContest, isPaidPurchase, attendeeState)

	return (
		<ContentModal
			open={open}
			setOpen={setOpen}
			isModal
			hideCloseButton={true}
			title={title}
		>
			<div className="relative flex flex-col items-center space-y-4">
				<div className="max-w-[304px]">
					<div className="flex justify-center items-center py-4 relative mb-4">
						<PieImage className="w-[168px] h-auto rounded-xl flex-shrink-0" images={[eventAvatarUrl]} />
						{attendeeState && !isContest && (
							<div className="absolute bottom-1.5">
								<RSVPStatus state={attendeeState} label={STATUS_LABEL[attendeeState]} />
							</div>
						)}
					</div>
					<div>
						{attendeeState === EventAttendeeState.InviteeAccepted && (
							<InviteeAcceptedBody
								isContest={isContest}
								isGettingFreeTicket={isGettingFreeTicket}
								isPaidPurchase={isPaidPurchase}
								contestName={contestName}
							/>
						)}
						{attendeeState === EventAttendeeState.Approved && (
							<ApprovedBody isContest={isContest} isContestPurchaseConfirmed={isContestPurchaseConfirmed} contestName={contestName} />
						)}
					</div>
				</div>
				{shareableLink && <ShareLinkCard url={shareableLink} />}
				{!isContest && (
					<a
						href="https://apps.apple.com/vn/app/loyalink/id1623230623"
						target="_blank"
						rel="noopener noreferrer"
						className="w-full text-center text-sm text-secondary-text underline"
					>
						Get the Loyalink app to access your tickets
					</a>
				)}
				<Button variant="secondary" fullWidth onClick={handleClose}>
					Close
				</Button>
			</div>
		</ContentModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface RSVPSuccessModalManagedProps {
	attendeeState?: EventAttendeeState | null
	eventAvatarUrl?: string | null
	isGettingFreeTicket?: boolean
	eventType?: EventType | null
	contestEntrySelected?: boolean
	shareableLink?: string | null
	contestName?: string | null
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'rsvp-success': RSVPSuccessModalManagedProps
	}
}

export function ManagedRSVPSuccessModal({
	onClose,
	...rest
}: RSVPSuccessModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(onClose, 200)
	}

	return (
		<RSVPSuccessModal
			open={open}
			setOpen={(v: boolean) => { if (!v) handleClose() }}
			onClose={handleClose}
			{...rest}
		/>
	)
}
